import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ServicePage } from "../components/service-page";
import { getService as getServiceSync } from "../data/services";

const SITE = "https://capitalpropressurewashing.com";

export const Route = createFileRoute("/$service")({
  loader: async ({ params }) => {
    const { getService } = await import("../data/services");
    const service = getService(params.service);
    if (!service) throw notFound();
    return {
      slug: service.slug,
      name: service.name,
      heroImage: service.heroImage,
      meta: service.meta,
      faqs: service.faqs,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Service Not Found | Capital Pro Pressure Washing" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `${SITE}/${params.service}`;
    const image = loaderData.heroImage.startsWith("http")
      ? loaderData.heroImage
      : `${SITE}${loaderData.heroImage}`;
    return {
      meta: [
        { title: loaderData.meta.title },
        { name: "description", content: loaderData.meta.description },
        { property: "og:title", content: loaderData.meta.title },
        { property: "og:description", content: loaderData.meta.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:image", content: image },
        { name: "twitter:title", content: loaderData.meta.title },
        { name: "twitter:description", content: loaderData.meta.description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        ...(params.service === "paver-cleaning-sealing"
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "LocalBusiness",
                  "@id": `${url}#localbusiness`,
                  name: "Capital Pro Pressure Washing",
                  url,
                  image,
                  telephone: "+15189001913",
                  priceRange: "$$",
                  description:
                    "Paver cleaning, polymeric re-sanding, and sealing for patios, walkways, and driveways across the Capital Region of New York.",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "5A Vatrano Rd",
                    addressLocality: "Albany",
                    addressRegion: "NY",
                    postalCode: "12205",
                    addressCountry: "US",
                  },
                  areaServed: [
                    { "@type": "City", name: "Albany", addressRegion: "NY" },
                    { "@type": "City", name: "Schenectady", addressRegion: "NY" },
                    { "@type": "City", name: "Clifton Park", addressRegion: "NY" },
                    { "@type": "City", name: "Saratoga Springs", addressRegion: "NY" },
                    { "@type": "City", name: "Troy", addressRegion: "NY" },
                    { "@type": "City", name: "Rensselaer", addressRegion: "NY" },
                  ],
                  parentOrganization: { "@id": `${SITE}/#business` },
                  hasOfferCatalog: {
                    "@type": "OfferCatalog",
                    name: "Paver Restoration Services",
                    itemListElement: [
                      "Paver Cleaning",
                      "Joint Blowout & Polymeric Re-Sanding",
                      "Paver Sealing",
                    ].map((n) => ({
                      "@type": "Offer",
                      itemOffered: { "@type": "Service", name: n },
                    })),
                  },
                }),
              },
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: loaderData.name,
            serviceType: loaderData.name,
            provider: { "@id": `${SITE}/#business` },
            areaServed: [
              "Albany, NY",
              "Schenectady, NY",
              "Clifton Park, NY",
              "Saratoga Springs, NY",
              "Troy, NY",
              "Rensselaer, NY",
            ],
            url,
            description: loaderData.meta.description,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: loaderData.name, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: loaderData.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: NotFound,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold text-[var(--brand-deep-blue)]">Something went wrong</h1>
      <p className="mt-2 text-gray-600">{error instanceof Error ? error.message : "Unexpected error"}</p>
      <button onClick={reset} className="mt-6 px-4 py-2 bg-[var(--brand-red)] text-white rounded">Try again</button>
    </div>
  ),
  component: ServiceRoute,
});

function ServiceRoute() {
  const { slug } = Route.useLoaderData();
  const service = getServiceSync(slug)!;
  return <ServicePage service={service} />;
}

function NotFound() {
  return (
    <main className="mx-auto max-w-xl px-4 py-24 text-center">
      <h1
        className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]"
        style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
      >
        Service not found
      </h1>
      <p className="mt-3 text-[var(--brand-charcoal)]/80">
        The page you're looking for doesn't exist.
      </p>
      <Link
        to="/"
        className="inline-block mt-6 px-5 py-3 bg-[var(--brand-red)] text-white font-bold uppercase rounded-md"
      >
        Back to Home
      </Link>
    </main>
  );
}